import { nextTick } from "vue";
import { TRANSITION_FOCUS_DELAY } from "./constants.js";

/**
 * Focus management utilities for supplement editor sections.
 * These helpers standardize focus behavior after DOM mutations (add, remove, toggle).
 */

/**
 * Focus the appropriate element after removing an item from a list.
 * Tries to focus the item at the same index, then previous item, then fallback element.
 *
 * @param {number} removedIndex - Index of the removed item
 * @param {string} itemSelector - CSS selector for focusable items in the list
 * @param {string} fallbackSelector - CSS selector for fallback element if list is empty
 */
export function focusAfterRemove(removedIndex, itemSelector, fallbackSelector) {
  nextTick(() => {
    const items = document.querySelectorAll(itemSelector);
    if (items[removedIndex]) {
      items[removedIndex].focus();
    } else if (items[removedIndex - 1]) {
      items[removedIndex - 1].focus();
    } else {
      document.querySelector(fallbackSelector)?.focus();
    }
  });
}

/**
 * Focus the last item in a list after adding a new item.
 * Uses TRANSITION_FOCUS_DELAY to allow CSS transitions to complete.
 *
 * @param {string} itemSelector - CSS selector for focusable items in the list
 * @param {Function} [onComplete] - Optional callback after focus is set
 */
export function focusAfterAdd(itemSelector, onComplete) {
  setTimeout(() => {
    const items = document.querySelectorAll(itemSelector);
    items[items.length - 1]?.focus();
    onComplete?.();
  }, TRANSITION_FOCUS_DELAY);
}

/**
 * Focus the first input in a section when the section is opened.
 * Uses TRANSITION_FOCUS_DELAY to allow expand animation to complete.
 *
 * @param {boolean} isOpen - Whether the section is opening
 * @param {string} inputSelector - CSS selector for the first input to focus
 */
export function focusOnSectionOpen(isOpen, inputSelector) {
  if (isOpen) {
    setTimeout(() => {
      document.querySelector(inputSelector)?.focus();
    }, TRANSITION_FOCUS_DELAY);
  }
}

export function scrollToSourceAfterSort(uid) {
  nextTick(() => {
    requestAnimationFrame(() => {
      const sourceInput = document.getElementById(`ingredient-source-${uid}`);
      if (!sourceInput) return;

      sourceInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
      sourceInput.focus({ preventScroll: true });
    });
  });
}
